import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { DollarSign, Gift, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CreditApplySection Component
 * 
 * Section in checkout to apply user credits to booking.
 */
const CreditApplySection = ({ 
  api, 
  bookingTotal, 
  onCreditApplied,
  appliedCredit = 0 
}) => {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [applyCredit, setApplyCredit] = useState(false);
  const [creditAmount, setCreditAmount] = useState(0);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await api.get('/users/credits');
        setBalance(response.data.balance || 0);
      } catch (error) {
        console.error('Error fetching credits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();
  }, [api]);

  useEffect(() => {
    if (applyCredit) {
      // Apply the maximum possible credit (up to booking total or balance)
      const maxApplicable = Math.min(balance, bookingTotal);
      setCreditAmount(maxApplicable);
      onCreditApplied?.(maxApplicable);
    } else {
      setCreditAmount(0);
      onCreditApplied?.(0);
    }
  }, [applyCredit, balance, bookingTotal, onCreditApplied]);

  if (loading) {
    return null;
  }

  if (balance <= 0) {
    return null;
  }

  return (
    <div 
      className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100"
      data-testid="credit-apply-section"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5 text-emerald-600" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-slate-900 text-sm">HireHub Credit Available</h4>
            <span className="font-bold text-emerald-600">${balance.toFixed(2)}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox 
              id="apply-credit"
              checked={applyCredit}
              onCheckedChange={setApplyCredit}
              data-testid="apply-credit-checkbox"
            />
            <label 
              htmlFor="apply-credit" 
              className="text-sm text-slate-600 cursor-pointer"
            >
              Apply ${Math.min(balance, bookingTotal).toFixed(2)} credit to this booking
            </label>
          </div>
          
          {applyCredit && (
            <div className="mt-2 flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle className="w-4 h-4" />
              <span>-${creditAmount.toFixed(2)} will be deducted from total</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * CreditBalanceCard Component
 * 
 * Display user's credit balance in dashboard.
 */
export const CreditBalanceCard = ({ api }) => {
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await api.get('/users/credits');
        setBalance(response.data.balance || 0);
        setHistory(response.data.history || []);
      } catch (error) {
        console.error('Error fetching credits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();
  }, [api]);

  if (loading) {
    return null;
  }

  if (balance <= 0 && history.length === 0) {
    return null;
  }

  return (
    <Card className="border-slate-100" data-testid="credit-balance-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Available Credit</p>
              <p className="text-2xl font-bold text-emerald-600">${balance.toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        {history.length > 0 && (
          <div className="border-t border-slate-100 pt-3 mt-3">
            <p className="text-xs text-slate-500 mb-2">Recent Activity</p>
            <div className="space-y-2">
              {history.slice(0, 3).map((credit) => (
                <div 
                  key={credit.credit_id}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-slate-600 truncate flex-1">{credit.reason}</span>
                  <span className={`font-medium ${credit.amount >= 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {credit.amount >= 0 ? '+' : ''}${Math.abs(credit.amount).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CreditApplySection;
